import { VenueReservation } from "@/graphql/operations";
import useDevice from "@/hooks/useDevice";
import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {
  Virtuoso,
  type VirtuosoHandle,
  type VirtuosoProps,
} from "react-virtuoso";
import ReservationRow from "../ReservationRow/ReservationRow";
import ReservationDayHeader from "./ReservationDayHeader";

type DayRow = { date: string; reservations: VenueReservation[] };

export type ScrollToDateOptions = {
  behavior?: Extract<ScrollBehavior, "auto" | "smooth">;
  align?: "start" | "center" | "end";
};

type VirtualReservationListProps = Pick<
  VirtuosoProps<DayRow, unknown>,
  | "endReached"
  | "startReached"
  | "atTopStateChange"
  | "components"
  | "initialTopMostItemIndex"
  | "firstItemIndex"
> & {
  dayRows: DayRow[];
  selectedReservation: VenueReservation | null;
  selectReservation: (reservationId: string) => void;
};

export type VirtualReservationListHandle = {
  scrollToDate: (date: string, options?: ScrollToDateOptions) => void;
};

const VirtualReservationList = forwardRef<
  VirtualReservationListHandle,
  VirtualReservationListProps
>(function VirtualReservationList(
  {
    dayRows,
    selectReservation,
    selectedReservation,
    firstItemIndex,
    ...virtuosoProps
  },
  ref,
) {
  const { isMobile } = useDevice();
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const pendingScrollRef = useRef<{
    date: string;
    options?: ScrollToDateOptions;
  } | null>(null);

  const heightEstimates = useMemo(() => {
    const base = 30;
    const perReservation = isMobile ? 76 : 68;
    return dayRows.map(
      (row) => base + perReservation * row.reservations.length,
    );
  }, [dayRows, isMobile]);

  const tryScrollToDate = useCallback(
    (date: string, options?: ScrollToDateOptions) => {
      const index = dayRows.findIndex((row) => row.date === date);
      if (index === -1) return false;
      virtuosoRef.current?.scrollToIndex({
        index,
        align: options?.align ?? "start",
        behavior: options?.behavior ?? "auto",
      });
      return true;
    },
    [dayRows],
  );

  const scrollToDate = useCallback(
    (date: string, options?: ScrollToDateOptions) => {
      pendingScrollRef.current = { date, options };
      if (tryScrollToDate(date, options)) pendingScrollRef.current = null;
    },
    [tryScrollToDate],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToDate,
    }),
    [scrollToDate],
  );

  useEffect(() => {
    if (!pendingScrollRef.current) return;
    const { date, options } = pendingScrollRef.current;
    if (tryScrollToDate(date, options)) {
      pendingScrollRef.current = null;
    }
  }, [tryScrollToDate]);

  return (
    <Virtuoso<DayRow>
      {...virtuosoProps}
      data={dayRows}
      firstItemIndex={firstItemIndex}
      computeItemKey={(_index, item) => item.date}
      heightEstimates={heightEstimates}
      skipAnimationFrameInResizeObserver
      overscan={800}
      ref={virtuosoRef}
      components={{
        ...virtuosoProps.components,
      }}
      itemContent={(_index, dayRow) => {
        return (
          <Box>
            <ReservationDayHeader
              date={dayRow.date}
              reservationsCount={dayRow.reservations.length}
            />
            {dayRow.reservations.length === 0 ? <Divider /> : null}
            {dayRow.reservations.map((reservation) => (
              <Box key={reservation.id} data-testid="virtuoso-item-list">
                <ReservationRow
                  reservation={reservation}
                  isSelected={selectedReservation?.id === reservation.id}
                  selectReservation={() => selectReservation(reservation.id)}
                />
                <Divider />
              </Box>
            ))}
          </Box>
        );
      }}
      data-testid="virtual-reservation-table"
    />
  );
});

VirtualReservationList.displayName = "VirtualReservationList";

export default VirtualReservationList;
